import store, { cloneDeepSerializable } from 'redux/store';
import { uploadFileOptions } from 'shared/utils';
import {
  setCurrentVideoId,
  updateLectureComponentFromAngular,
  updateAutoGeneratedTranscript,
  updateAutoGeneratedCaption,
} from 'redux/actions/lecture-components';
import { AUTOMATED_TRANSLATION_LANGUAGES } from 'automated_translation/components/language-picker';
import { TranscriptStatusBadge, CaptionStatusBadge } from 'lecture_pages/components/video/auto-generated-status-badge';
import { useAngularPreventLecturePageNavigation } from '../hooks/use-prevent-lecture-page-navigation';

const ERROR_WHILE_REPLACING_VIDEO = 'replaced_video';

/* @ngInject */
export default function IndividualVideoEditController(
  $rootScope,
  $scope,
  $stateParams,
  $timeout,
  $uibModalInstance,
  $window,
  AlertMessages,
  ConfirmationOverlays,
  CurrentCourseManager,
  S3NameSpaces,
  S3UploadFactory,
  StateManager,
  Upload,
  config,
  lectureVideo,
  liveSessionId,
  nvUtil,
  recording,
  onSuccessfulEdit,
  _,
) {
  $scope.TranscriptStatusBadge = TranscriptStatusBadge;
  $scope.CaptionStatusBadge = CaptionStatusBadge;
  const vm = this;

  vm.lectureVideo = lectureVideo;
  vm.recording = recording;
  vm.liveSessionId = liveSessionId;
  vm.onSuccessfulEdit = onSuccessfulEdit;
  vm.isLiveEventRecording = !!recording;
  vm.videoDraft = vm.isLiveEventRecording ? { ...recording } : { ...lectureVideo.video };
  vm.CurrentCourseManager = CurrentCourseManager;
  vm.config = config;
  vm.languages = _.pluck(AUTOMATED_TRANSLATION_LANGUAGES, 'translationKey');
  vm.fileMaxSize = `${config.files.rteFileSizeLimit}MB`;
  vm.thumbnailMaxSize = `${config.files.smallFileSizeLimit}MB`;
  const recordingSizeLimitGb = 6;
  vm.maxRecordingFileSize = `${recordingSizeLimitGb}GB`;
  vm.deletedCaptions = [];
  vm.documentType = null;
  vm.deleteDocumentType = null;
  vm.isDeleteDocumentAutoGenerated = false;
  vm.deleteCaptionsIndex = -1;
  vm.uploadFileOptions = uploadFileOptions;
  vm.cancelDocumentType = null;
  vm.autoGenerateButton = {
    transcript: {
      show: false,
      errorCode: '',
      tooltip: '',
    },
    captions: {
      show: false,
      errorCode: '',
      tooltip: '',
    },
  };
  vm.replacingVideo = false;
  let dismissedByUser = false;
  let isModalOpen = true;
  let enablingTranscriptAutoGeneration = false;
  let enablingCaptionAutoGeneration = false;

  vm.documentTypes = {
    slides: 'LECTURE_VIDEO.DOCUMENT_TYPE_SLIDES',
    transcript: 'LECTURE_VIDEO.DOCUMENT_TYPE_TRANSCRIPT',
    customThumbnail: 'LECTURE_VIDEO.DOCUMENT_TYPE_CUSTOM_THUMBNAIL',
    captions: 'LECTURE_VIDEO.DOCUMENT_TYPE_CAPTIONS',
  };

  vm.allowedFileTypes = {
    slides: vm.config.files.rte.presentations.extensions,
    transcript: vm.config.files.rte.documents.extensions,
    captions: vm.config.files.subtitles.split(','),
    customThumbnail: vm.config.files.rte.images.extensions,
    video: vm.config.files.allVideoExtensions,
    audio: vm.config.files.allAudioExtensions,
  };

  vm.filePatterns = {
    slides: config.files.presentations,
    transcript: config.files.documents,
    customThumbnail: config.files.images,
    captions: config.files.subtitles,
  };

  vm.filterLanguages = (language) => !vm.videoDraft.captions.some((cap) => cap.language?.toLowerCase() === language?.toLowerCase());

  // Getting the institution info to know if the auto generation process is enabled
  const { currentInstitutionId } = store.getState().app;
  const currentInstitution = store.getState().models.institutions[currentInstitutionId];

  // Synchronization of auto-generated process information when enabled for video
  store.dispatch(setCurrentVideoId({
    videoId: vm.videoDraft.id,
  }));

  function updateVideoFromRedux() {
    const videoFromRedux = store.getState().models.video[vm.videoDraft.id];

    if (videoFromRedux) {
      vm.videoDraft = cloneDeepSerializable(videoFromRedux);
      vm.autoGeneratedTranscript = {
        isEnabled: videoFromRedux?.autoGenerateTranscript ?? false,
        data: videoFromRedux?.autoGenerated?.transcript ?? {},
      };
      vm.autoGeneratedCaption = {
        isEnabled: videoFromRedux?.autoGenerateCaption ?? false,
        data: videoFromRedux?.autoGenerated?.caption ?? {},
      };
    } else {
      vm.autoGeneratedTranscript = {
        isEnabled: false,
        data: {},
      };
      vm.autoGeneratedCaption = {
        isEnabled: false,
        data: {},
      };
    }
  }
  updateVideoFromRedux();

  // Setting up the captions files information:
  // 1. A new one is being loaded.
  // 2. A file already exists.
  // 3. An auto-generated file is being generated, will be generated, was generated.
  function updateCaptionsInfo(video) {
    vm.captions = {
      hasFiles: (vm.autoGeneratedCaption.isEnabled && !vm.autoGeneratedCaption.data?.errorCode) || (video.captions && video.captions.length && !video.captions.$error),
      errorCode: vm.autoGeneratedCaption.data?.errorCode || '',
    };

    if (vm.captions.hasFiles) {
      const videoManualCaptions = video?.captions.map((caption, index) => ({
        index,
        fileName: caption?.file?.name || caption?.fileName,
        language: caption?.language,
        fileUrl: caption.fileUrl,
        isAutoGenerated: false,
      }));

      const videoAutoGeneratedCaption = (
        vm.autoGeneratedCaption.isEnabled && !vm.autoGeneratedCaption.data?.errorCode
      ) ? [{
          ...vm.autoGeneratedCaption.data,
          index: 0,
          fileName: vm.autoGeneratedCaption.data?.fileName,
          fileUrl: vm.autoGeneratedCaption.data?.url,
          isAutoGenerated: true,
        }] : [];

      vm.captions.files = [
        ...videoAutoGeneratedCaption,
        ...videoManualCaptions,
      ];
    }
  }
  // Setting up the centralized captions info
  updateCaptionsInfo(vm.videoDraft);

  // Setting up the transcript file information:
  // 1. A new one is being loaded.
  // 2. A file already exists.
  // 3. An auto-generated file is being generated, will be generated, was generated.
  function updateTranscriptInfo(video) {
    vm.transcript = {
      hasFile: (vm.autoGeneratedTranscript.isEnabled && !vm.autoGeneratedTranscript.data?.errorCode) || video.textTranscriptFileName || (video.textTranscript && !video.textTranscript.$error),
      isAutoGenerated: vm.autoGeneratedTranscript.isEnabled && !vm.autoGeneratedTranscript.data?.errorCode,
      fileName: video.textTranscriptFileName || video.textTranscript?.name,
      fileUrl: video.textTranscriptUrl || video.transcriptUrl,
      errorCode: vm.autoGeneratedTranscript.data?.errorCode || '',
    };

    if (vm.transcript.isAutoGenerated && !vm.transcript.errorCode) {
      vm.transcript.fileName = vm.autoGeneratedTranscript.data?.fileName;
      vm.transcript.fileUrl = vm.autoGeneratedTranscript.data?.url;
      vm.transcript.status = vm.autoGeneratedTranscript.data?.status;
    }
  }
  // Setting up the centralized transcript info
  updateTranscriptInfo(vm.videoDraft);

  // Logic to know if we should show the auto generate button
  function showAutoGenerateButton() {
    if (!currentInstitution.speechToTextEnabled) {
      vm.autoGenerateButton = {
        transcript: {
          show: false,
          errorCode: '',
          tooltip: '',
        },
        captions: {
          show: false,
          errorCode: '',
          tooltip: '',
        },
      };
    } else {
      switch (vm.documentType) {
        case 'captions':
          vm.autoGenerateButton = {
            ...vm.autoGenerateButton,
            captions: {
              show: !vm.autoGeneratedCaption.isEnabled || !!vm.autoGeneratedCaption.data?.errorCode || vm.replacingVideo,
              errorCode: vm.autoGeneratedCaption.data?.errorCode || (vm.replacingVideo && ERROR_WHILE_REPLACING_VIDEO),
              tooltip: (vm.autoGeneratedCaption.data?.errorCode || vm.replacingVideo) ? `LECTURE_VIDEO.AUTO_GENERATED_TRANSCRIPT_AND_CAPTIONS.TOOLTIP.${vm.autoGeneratedCaption.data?.errorCode ? vm.autoGeneratedCaption.data.errorCode.toUpperCase() : ERROR_WHILE_REPLACING_VIDEO.toUpperCase()}` : '',
            },
          };
          break;
        case 'transcript':
          vm.autoGenerateButton = {
            ...vm.autoGenerateButton,
            transcript: {
              show: !vm.autoGeneratedTranscript.isEnabled || !!vm.autoGeneratedTranscript.data?.errorCode || vm.replacingVideo,
              errorCode: vm.autoGeneratedTranscript.data?.errorCode || (vm.replacingVideo && ERROR_WHILE_REPLACING_VIDEO),
              tooltip: (vm.autoGeneratedTranscript.data?.errorCode || vm.replacingVideo) ? `LECTURE_VIDEO.AUTO_GENERATED_TRANSCRIPT_AND_CAPTIONS.TOOLTIP.${vm.autoGeneratedTranscript.data?.errorCode ? vm.autoGeneratedTranscript.data.errorCode.toUpperCase() : ERROR_WHILE_REPLACING_VIDEO.toUpperCase()}` : '',
            },
          };
          break;
        default:
          break;
      }
    }
  }

  vm.onLanguageSelected = (index, lang) => {
    vm.videoDraft.captions[index].language = lang;
    vm.videoEditForm.$setDirty();

    // Updating the centralized captions info
    updateCaptionsInfo(vm.videoDraft);
  };

  vm.setDocumentType = (type) => {
    vm.documentType = type;

    // Updating the auto-generate button state
    showAutoGenerateButton();
  };

  vm.canAdd = (type) => {
    switch (type) {
      case 'slides':
        return (!vm.videoDraft.slidesFileName && !vm.videoDraft.slides?.name) || vm.videoDraft.slides?.$error;
      case 'transcript':
        return !vm.transcript.hasFile;
      case 'customThumbnail':
        return (!vm.videoDraft.customThumbnailFileName && !vm.videoDraft.customThumbnail?.name) || vm.videoDraft.customThumbnail?.$error;
      case 'captions':
        return vm.videoDraft.captions && vm.videoDraft.captions.length < vm.languages.length;
      default:
        return false;
    }
  };

  vm.captionsMissingLanguage = () => vm.videoDraft.captions.some(caption => !caption.language);

  // Used by live-session-edit-recording-modal; copies a function on lecture-video-model
  vm.hasValidVideoUrl = () => vm.videoDraft.lowQualityUrl || vm.videoDraft.mediumQualityUrl || (vm.videoDraft.type === 'video/mp4' && vm.videoDraft.originalUrl);

  vm.saveFile = function ($file, type, $invalidFiles) {
    if ($invalidFiles?.length) {
      if ($invalidFiles.find(file => file.$error === 'maxSize')) {
        AlertMessages.error('FORM.OOPS', 'FILE_UPLOAD.SIZE_EXCEEDED_ALERT', {}, { sizeLimit: vm.documentType === 'customThumbnail' ? vm.thumbnailMaxSize : vm.fileMaxSize });
      } else {
        AlertMessages.error('FORM.OOPS', 'FILE_UPLOAD.NOT_SUPPORTED_ALERT');
      }
    } else if ($file.name.includes('.')) {
      const extension = $file.name.split('.').pop();

      if (!vm.allowedFileTypes[type].includes(`.${extension.toLowerCase()}`)) {
        vm.videoEditForm.$dirty = false;
        $timeout(() => {
          AlertMessages.error('FORM.OOPS', 'FILE_UPLOAD.NOT_SUPPORTED_ALERT');
        });
        vm.videoDraft[vm.uploadFileOptions[vm.documentType].backendName] = null;
        vm.videoDraft[`${vm.uploadFileOptions[vm.documentType].backendName}FileName`] = null;
      } else {
        const url = URL.createObjectURL($file);
        if (vm.documentType === 'captions') {
          vm.videoDraft.captions.push({ file: $file, fileUrl: url });

          // Updating the centralized captions info
          updateCaptionsInfo(vm.videoDraft);
        } else {
          vm.videoDraft[vm.uploadFileOptions[vm.documentType].backendName] = $file;
          vm.videoDraft[`${type}Url`] = url;

          // Updating the centralized transcript info
          if (vm.documentType === 'transcript') {
            updateTranscriptInfo(vm.videoDraft);
          }
        }
        vm.videoEditForm.$setDirty();
        vm.documentType = null;
      }
    }
    vm.savingFile = false;
  };

  // Process to start/cancel the auto generation of a transcript or caption
  vm.handleAutoGeneration = () => {
    // Logic to start the auto generation
    if (vm.documentType) {
      switch (vm.documentType) {
        case 'captions':
          enablingCaptionAutoGeneration = true;
          vm.autoGeneratedCaption.isEnabled = true;
          break;
        case 'transcript':
          enablingTranscriptAutoGeneration = true;
          vm.autoGeneratedTranscript.isEnabled = true;
          break;
        default:
          break;
      }
    }

    // Logic to cancel the auto generation
    if (vm.cancelDocumentType) {
      switch (vm.cancelDocumentType) {
        case 'caption':
          vm.autoGeneratedCaption.isEnabled = false;
          break;
        case 'transcript':
          vm.autoGeneratedTranscript.isEnabled = false;
          break;
        default:
          break;
      }
    }

    // Showing the info message for starting the auto generation
    if (vm.documentType) {
      switch (vm.documentType) {
        case 'captions':
          AlertMessages.info(
            'LECTURE_VIDEO.AUTO_GENERATED_TRANSCRIPT_AND_CAPTIONS.INFO.TITLE.CAPTIONS',
            'LECTURE_VIDEO.AUTO_GENERATED_TRANSCRIPT_AND_CAPTIONS.INFO.MESSAGE.UPDATE',
          );
          break;
        case 'transcript':
          AlertMessages.info(
            'LECTURE_VIDEO.AUTO_GENERATED_TRANSCRIPT_AND_CAPTIONS.INFO.TITLE.TRANSCRIPT',
            'LECTURE_VIDEO.AUTO_GENERATED_TRANSCRIPT_AND_CAPTIONS.INFO.MESSAGE.UPDATE',
          );
          break;
        default:
          break;
      }
    }

    // Reseting the values
    vm.documentType = null;
    vm.cancelDocumentType = null;
    vm.autoGenerateButton = {
      transcript: {
        show: false,
        errorCode: '',
        tooltip: '',
      },
      captions: {
        show: false,
        errorCode: '',
        tooltip: '',
      },
    };

    // Updating the video with the updated payload
    vm.updateSingleVideo(vm.videoDraft, false);
  };

  vm.deleteDocument = () => {
    switch (vm.deleteDocumentType) {
      case 'slides':
        vm.onDelete('slides', 'removeSlides');
        break;
      case 'transcript':
        if (vm.isDeleteDocumentAutoGenerated) {
          vm.onDelete('autoGenerateTranscript');
        } else {
          vm.onDelete('textTranscript', 'removeTextTranscript');
        }
        break;
      case 'customThumbnail':
        vm.onDelete('customThumbnail', 'removeCustomThumbnail');
        break;
      case 'captions':
        if (vm.isDeleteDocumentAutoGenerated) {
          vm.onDelete('autoGenerateCaption');
        } else {
          vm.onDelete('captions', 'removeCaptions', vm.deleteCaptionsIndex);
        }
        break;
      default:
        break;
    }

    // Reseting the values
    vm.deleteDocumentType = null;
    vm.isDeleteDocumentAutoGenerated = false;
    vm.deleteCaptionsIndex = -1;
  };

  vm.onDelete = function (type, attr, captionsIndex) {
    function resetValues() {
      // Checking if file was saved
      if (vm.videoDraft[`${type}Url`]) {
        vm.videoDraft[attr] = true;
      }

      // Reseting the values in videoDraft
      vm.videoDraft[type] = null;
      vm.videoDraft[`${type}FileName`] = null;

      // Comparing the videoDraft against the initial video data
      const initialData = vm.lectureVideo?.video[`${type}FileName`];
      const draftData = vm.videoDraft[`${type}FileName`];
      if (initialData === draftData) {
        vm.videoEditForm.$dirty = false;
      } else {
        vm.videoEditForm.$setDirty();
      }
    }

    switch (type) {
      case 'captions':
        if (captionsIndex !== -1) {
          const captionToRemove = vm.videoDraft.captions.splice(captionsIndex, 1)[0];
          if (captionToRemove?.id) {
            vm.deletedCaptions.push({ ...captionToRemove, _destroy: true });
            vm.videoEditForm.$setDirty();
          } else {
            vm.videoEditForm.$dirty = false;
          }
          updateCaptionsInfo(vm.videoDraft);
        }
        break;
      case 'autoGenerateCaption':
        vm.videoDraft.autoGenerateCaption = false;
        vm.autoGeneratedCaption.isEnabled = false;
        vm.videoEditForm.$setDirty();
        updateCaptionsInfo(vm.videoDraft);
        break;
      case 'textTranscript':
        resetValues();
        updateTranscriptInfo(vm.videoDraft);
        break;
      case 'autoGenerateTranscript':
        vm.videoDraft.autoGenerateTranscript = false;
        vm.autoGeneratedTranscript.isEnabled = false;
        vm.videoEditForm.$setDirty();
        updateTranscriptInfo(vm.videoDraft);
        break;
      default:
        resetValues();
        break;
    }
  };

  vm.updateSingleVideo = function (video, shouldCloseModal = true) {
    const update = () => {
      vm.uploadInProgress = true;
      const payload = {};

      // Live Session Recordings use `isDownloadable` instead of `canDownload`
      const payloadKeys = ['id', 'name', 'canDownload', 'isDownloadable', 'uniqueId', 'fileName', 'slides', 'captions', 'transcript', 'textTranscript', 'customThumbnail', 'removeSlides', 'removeTranscript', 'removeTextTranscript', 'removeCustomThumbnail', 'numCoursesUsing', 'recentCoursesUsing', 'autoGenerateTranscript', 'autoGenerateCaption', 'replaced'];
      payloadKeys.forEach(key => {
        switch (key) {
          case 'autoGenerateCaption':
            payload[nvUtil.toSnakeCase(key)] = vm.autoGeneratedCaption.isEnabled ?? video[key];
            break;
          case 'autoGenerateTranscript':
            payload[nvUtil.toSnakeCase(key)] = vm.autoGeneratedTranscript.isEnabled ?? video[key];
            break;
          case 'replaced':
            payload[nvUtil.toSnakeCase(key)] = vm.replacingVideo;
            payload[nvUtil.toSnakeCase('lecturePageId')] = $stateParams.id;
            vm.replacingVideo = false;
            break;
          default:
            payload[nvUtil.toSnakeCase(key)] = video[key];
            break;
        }
      });

      payload.captions = payload.captions.concat(vm.deletedCaptions);

      payload.captions.forEach((cap, index) => {
        delete payload.captions[index].$$hashKey;
        delete payload.captions[index].fileUrl;
      });

      const url = vm.isLiveEventRecording ? `${$stateParams.catalogId}/live_session/${vm.liveSessionId}/update_video` : `${$stateParams.catalogId}/videos/${video.id}`;
      const method = vm.isLiveEventRecording ? 'POST' : 'PUT';

      return Upload.upload({
        url,
        method,
        fields: {
          video: payload,
        },
      }).then((response) => {
        if (vm.isLiveEventRecording) {
          Object.assign(vm.recording, response.data.result.file);
          vm.onSuccessfulEdit?.(response.data.result);
        } else {
          if (vm.lectureVideo.videoList.defaultTitle) {
            vm.lectureVideo.videoList.content = vm.lectureVideo.video.name;
          }

          // Getting the auto-generated data from the response
          const autoGeneratedTranscript = response.data.result.autoGenerated?.transcript;
          const autoGeneratedCaption = response.data.result.autoGenerated?.caption;

          Object.assign(
            vm.lectureVideo.video,
            response.data.result,
            {
              captions: response.data.result.captions.map(cap => ({ ...cap })),
              autoGenerated: {
                transcript: {
                  ...autoGeneratedTranscript,
                  status: autoGeneratedTranscript?.status === 'regenerating' ? 'in_progress' : autoGeneratedTranscript?.status,
                },
                caption: {
                  ...autoGeneratedCaption,
                  status: autoGeneratedCaption?.status === 'regenerating' ? 'in_progress' : autoGeneratedCaption?.status,
                },
              },
            },
          );

          store.dispatch(updateLectureComponentFromAngular({
            lectureVideos: [{
              id: vm.lectureVideo.id,
              video: response.data.result,
            }],
          }));

          // Updating the Redux auto-generated data
          store.dispatch(updateAutoGeneratedTranscript({
            videoId: vm.lectureVideo.id,
            params: vm.lectureVideo.video.autoGenerated?.transcript || {},
          }));

          store.dispatch(updateAutoGeneratedCaption({
            videoId: vm.lectureVideo.id,
            params: vm.lectureVideo.video.autoGenerated?.caption || {},
          }));

          const enablingAutoGeneration = enablingTranscriptAutoGeneration || enablingCaptionAutoGeneration;

          if (!enablingAutoGeneration) {
            // Updating the centralized video data
            updateVideoFromRedux();
          } else {
            if (enablingTranscriptAutoGeneration) {
              vm.videoDraft.autoGenerateTranscript = vm.lectureVideo.video.autoGenerateTranscript;
              vm.videoDraft.autoGenerated.transcript = vm.lectureVideo.video.autoGenerated.transcript;
              vm.autoGeneratedTranscript = {
                isEnabled: vm.lectureVideo.video.autoGenerateTranscript,
                data: autoGeneratedTranscript,
              };
              enablingTranscriptAutoGeneration = false;
            }
            if (enablingCaptionAutoGeneration) {
              vm.videoDraft.autoGenerateCaption = vm.lectureVideo.video.autoGenerateCaption;
              vm.videoDraft.autoGenerated.caption = vm.lectureVideo.video.autoGenerated.caption;
              vm.autoGeneratedCaption = {
                isEnabled: vm.lectureVideo.video.autoGenerateCaption,
                data: autoGeneratedCaption,
              };
              enablingCaptionAutoGeneration = false;
            }
          }

          updateTranscriptInfo(vm.lectureVideo.video);
          updateCaptionsInfo(vm.lectureVideo.video);
          $rootScope.$broadcast('videoCaptionUpdated', vm.lectureVideo.video);
        }

        if (shouldCloseModal) {
          dismissedByUser = true;
          isModalOpen = false;
          $uibModalInstance.close();
        } else {
          vm.uploadInProgress = false;
        }
      }, (response) => {
        // error handling
        vm.uploadInProgress = false;
        AlertMessages.error('FORM.OOPS', 'FORM.ERROR_SOMETHING_WRONG');
      });
    };

    // Entry point for updating a single video. Checking if the video is being used in other courses and checking if the video also has auto-generated captions.
    const autoGeneratedCaptionLanguage = video.autoGenerateCaption && video.autoGenerated?.caption && video.autoGenerated.caption.language?.translationKey;
    const languageMatched = video.autoGenerateCaption && video.autoGenerated?.caption && video.captions.some(caption => caption.language.toLowerCase() === autoGeneratedCaptionLanguage?.toLowerCase());

    if (video.numCoursesUsing > 1) {
      const courses = video.recentCoursesUsing.filter((course) => course.catalogId !== vm.CurrentCourseManager.course.catalogId).slice(0, 3);

      ConfirmationOverlays.openConfirmationModal(
        'lecture_pages/templates/components/edit-shared-audio-video-confirmation-overlay.html',
        'AttachModalResolvesToVmCtrl',
        {
          vmResolves() {
            return {
              courses,
              isAudio: video.type === 'Audio',
              isProgram: vm.CurrentCourseManager.course.isProgram,
              numCoursesUsing: video.numCoursesUsing - 1,
              numOthers: video.numCoursesUsing - courses.length - 1,
            };
          },
        },
      ).result.then(update);
    } else if (video.autoGenerateCaption && languageMatched) {
      ConfirmationOverlays.openConfirmationModal(
        'lecture_pages/templates/replace-auto-generated-confirmation-overlay.html',
      ).result.then(() => {
        vm.autoGeneratedCaption.isEnabled = false;
        update();
      });
    } else {
      update();
    }
  };

  vm.uploadFile = ($file, $invalidFiles) => {
    if ($invalidFiles?.length) {
      if ($invalidFiles[0].$error === 'maxSize') {
        AlertMessages.error('FORM.OOPS', 'FILE_UPLOAD.SIZE_EXCEEDED_ALERT', {}, { sizeLimit: $invalidFiles[0].$errorParam });
      } else {
        AlertMessages.error('FORM.OOPS', 'FILE_UPLOAD.NOT_SUPPORTED_ALERT');
      }
    } else if ($file) {
      let valid = true;

      if ($file.name.includes('.')) {
        const extension = $file.name.split('.').pop();

        const isAudio = vm.videoDraft.type === 'Audio';
        if (!vm.allowedFileTypes[isAudio ? 'audio' : 'video'].includes(`.${extension.toLowerCase()}`)) {
          $timeout(() => {
            AlertMessages.error('FORM.OOPS', 'FILE_UPLOAD.NOT_SUPPORTED_ALERT');
          });
          valid = false;
        }
      }

      if (valid && $file.size > (vm.isLiveEventRecording ? recordingSizeLimitGb : config.files.videoFileSizeLimitGb) * 1024 * 1024 * 1024) {
        $timeout(() => {
          AlertMessages.error('FORM.OOPS', 'FILE_UPLOAD.SIZE_EXCEEDED_ALERT', {}, { sizeLimit: $file.$errorParam });
        });
        valid = false;
      }

      if (valid) {
        uploadVideoFile($file);
      }
    }
  };

  function uploadVideoFile(file) {
    vm.videoEditForm.$setDirty();
    const nameSpace = vm.isLiveEventRecording ? S3NameSpaces.ATTACHMENTS : S3NameSpaces.VIDEOS;
    vm.newVideoS3UploadObj = S3UploadFactory.uploadToS3(file, nameSpace, true);

    // If the current video has an auto-generated transcript
    if (vm.videoDraft?.autoGenerateTranscript) {
      // Setting the transcription state to 'regenerating'
      store.dispatch(updateAutoGeneratedTranscript({
        params: {
          ...vm.autoGeneratedTranscript.data,
          status: vm.autoGeneratedTranscript.isEnabled ? 'regenerating' : 'in_progress',
        },
      }));

      updateVideoFromRedux();
      updateTranscriptInfo(vm.videoDraft);
    }

    // If the current video has an auto-generated caption
    if (vm.videoDraft?.autoGenerateCaption) {
      // Setting the caption state to 'regenerating'
      store.dispatch(updateAutoGeneratedCaption({
        params: {
          ...vm.autoGeneratedCaption.data,
          status: vm.autoGeneratedCaption.isEnabled ? 'regenerating' : 'in_progress',
        },
      }));

      updateVideoFromRedux();
      updateCaptionsInfo(vm.videoDraft);
    }

    vm.newVideoS3UploadObj.promise.then((response) => {
      Object.assign(vm.videoDraft, response.config.data.file, { name: response.config.data.file.name });
      vm.videoDraft.fileName = vm.videoDraft.name;
      // eslint-disable-next-line no-useless-escape
      vm.videoDraft.name = vm.videoDraft.name.replace(/\.[^\/.]+$/, '');
    }, (response) => {
    });
  }

  vm.dataQaTagForCaptionLanguage = (index, lang) => {
    const label = vm.isLiveEventRecording ? 'EDIT_RECORDING_CAPTIONS_' : 'EDIT_VIDEO_CAPTIONS_';
    return `${label}${index}_${lang.toUpperCase()}`;
  };

  vm.dataQaTagForDownloadCaption = (index) => {
    const label = vm.isLiveEventRecording ? 'EDIT_RECORDING_DOWNLOAD_CAPTIONS_' : 'EDIT_VIDEO_DOWNLOAD_CAPTIONS_';
    return `${label}${index}`;
  };

  vm.dataQaTagForDeleteCaption = (index) => {
    const label = vm.isLiveEventRecording ? 'EDIT_RECORDING_DELETE_CAPTIONS_' : 'EDIT_VIDEO_DELETE_CAPTIONS_';
    return `${label}${index}`;
  };

  function hasUnsavedChanges() {
    return vm.videoEditForm?.$dirty;
  }

  const checkUnsavedChanges = () => hasUnsavedChanges();

  useAngularPreventLecturePageNavigation($scope, checkUnsavedChanges);

  // warn if leaving the modal and have unsaved changes
  vm.deregisterStateChangeStart = StateManager.registerStateChangeStart(checkUnsavedChanges,
    'discussions/templates/nv-unsaved-changes-confirmation-overlay.html',
    'FORM.NAVIGATE_AWAY.CLOSE_WINDOW',
    undefined,
    undefined,
    () => {
      vm.deregisterModalClosing?.();
      $uibModalInstance.dismiss();
    });

  vm.deregisterModalClosing = $scope.$on('modal.closing', ($event) => {
    if (hasUnsavedChanges() && !dismissedByUser) {
      $event.preventDefault();

      const modalOverlay = vm.isLiveEventRecording
        ? ConfirmationOverlays.openConfirmationModal('lecture_pages/templates/live-session-edit-recording-unsaved-changes-warning-overlay.html')
        : ConfirmationOverlays.openConfirmationModal('discussions/templates/nv-unsaved-changes-confirmation-overlay.html');

      modalOverlay.result.then(() => {
        dismissedByUser = true;
        isModalOpen = false;
        $uibModalInstance.dismiss();

        // If the current video has an auto-generated transcript and the current state is 'regenerating'
        if (vm.autoGeneratedTranscript.isEnabled && vm.autoGeneratedTranscript.data.status === 'regenerating') {
          // Resetting the transcription state to 'completed'
          store.dispatch(updateAutoGeneratedTranscript({
            params: {
              ...vm.autoGeneratedTranscript.data,
              status: 'completed',
            },
          }));

          updateVideoFromRedux();
          updateTranscriptInfo(vm.videoDraft);
        }

        // If the current video has an auto-generated caption and the current state is 'regenerating'
        if (vm.autoGeneratedCaption.isEnabled && vm.autoGeneratedCaption.data.status === 'regenerating') {
          // Resetting the caption state to 'completed'
          store.dispatch(updateAutoGeneratedCaption({
            params: {
              ...vm.autoGeneratedCaption.data,
              status: 'completed',
            },
          }));

          updateVideoFromRedux();
          updateCaptionsInfo(vm.videoDraft);
        }
      });
    } else {
      isModalOpen = false;
    }
  });

  $rootScope.$on('video-transcript-updated', (event, transcriptData) => {
    // We need the timeout here so the modal can be re-rendered
    $timeout(() => {
      // At this point the Redux state has the updated data via Pusher
      const videoFromRedux = store.getState().models.video[vm.videoDraft.id];

      // Updating the Angular video draft information
      vm.videoDraft.autoGenerated.transcript = transcriptData;
      vm.videoDraft.autoGenerated.caption = videoFromRedux.autoGenerated.caption;
      vm.autoGeneratedTranscript = {
        isEnabled: vm.videoDraft.autoGenerateTranscript ?? false,
        data: transcriptData,
      };
      vm.autoGeneratedCaption = {
        isEnabled: vm.videoDraft.autoGenerateCaption ?? false,
        data: videoFromRedux.autoGenerated.caption,
      };

      updateTranscriptInfo(vm.videoDraft);
      updateCaptionsInfo(vm.videoDraft);

      if (transcriptData?.errorCode && isModalOpen) {
        AlertMessages.info(
          'LECTURE_VIDEO.AUTO_GENERATED_TRANSCRIPT_AND_CAPTIONS.INFO.TITLE.ERROR_GENERAL',
          `LECTURE_VIDEO.AUTO_GENERATED_TRANSCRIPT_AND_CAPTIONS.INFO.MESSAGE.${transcriptData.errorCode.toUpperCase()}`,
        );
      }
    });
  });

  $rootScope.$on('video-caption-updated', (event, captionData) => {
    // We need the timeout here so the modal can be re-rendered
    $timeout(() => {
      // At this point the Redux state has the updated data via Pusher
      const videoFromRedux = store.getState().models.video[vm.videoDraft.id];

      // Updating the Angular video draft information
      vm.videoDraft.autoGenerated.transcript = videoFromRedux.autoGenerated.transcript;
      vm.videoDraft.autoGenerated.caption = captionData;
      vm.autoGeneratedTranscript = {
        isEnabled: vm.videoDraft.autoGenerateTranscript ?? false,
        data: videoFromRedux.autoGenerated.transcript,
      };
      vm.autoGeneratedCaption = {
        isEnabled: vm.videoDraft.autoGenerateCaption ?? false,
        data: captionData,
      };

      updateTranscriptInfo(vm.videoDraft);
      updateCaptionsInfo(vm.videoDraft);

      // Updating the Angular lecture video information
      Object.assign(
        vm.lectureVideo.video,
        videoFromRedux,
        { captions: videoFromRedux.captions.map(cap => ({ ...cap })) },
      );

      // Showing the error message if the user has the video edit modal open
      if (captionData?.errorCode && isModalOpen) {
        AlertMessages.info(
          `LECTURE_VIDEO.AUTO_GENERATED_TRANSCRIPT_AND_CAPTIONS.INFO.TITLE.${captionData.errorCode === 'duplicated_language' ? 'ERROR_DUPLICATED_LANGUAGE' : 'ERROR_GENERAL'}`,
          `LECTURE_VIDEO.AUTO_GENERATED_TRANSCRIPT_AND_CAPTIONS.INFO.MESSAGE.${captionData.errorCode.toUpperCase()}`,
        );
      }
    });
  });

  $scope.$on('$destroy', () => {
    angular.element($window).off('beforeunload');
    vm.deregisterStateChangeStart?.();
  });
}
